// <rts-fulfillment-toggle> — swaps the product form's variant ID between
// the currently-selected Standard variant and its RTS (Ready to Ship)
// counterpart based on a radio choice.
//
// Server-side (snippets/rts-fulfillment-toggle.liquid) pre-computes a
// JSON map of { standardVariantId: rtsVariantId } for every standard
// variant that has an in-stock RTS twin (matched by variant.title, which
// is Shopify's " / "-joined option string). International shoppers get
// an empty map because the RTS companion product is only published to
// the "US Ready to Ship" Market catalog — its variants report
// available: false outside the US and Liquid skips them.
//
// The toggle listens for variant:change via the theme's PubSub. When
// the user picks a new size/drill/kit, we look up the new standard
// variant in the map:
//   - No RTS twin available → hide the toggle, force Standard, restore
//     input[name="id"] to the standard variant.
//   - RTS twin available → show the toggle, and if the user's current
//     preference is Ready to Ship, overwrite input[name="id"] with the
//     RTS variant ID so the form submits the correct one.
//
// The RTS variant ID is a real Shopify variant — the cart routes through
// Shopify's own market/catalog enforcement, so we're not faking anything
// with line-item properties.

class RtsFulfillmentToggle extends HTMLElement {
  constructor() {
    super();
    // Default to Ready to Ship. The Liquid map is only populated when the
    // shopper is in a market where the RTS companion is published (US) and
    // the specific variant is in stock at Arkansas — so defaulting to RTS
    // is safe: if no map entry exists for the current variant, applyState()
    // hides the toggle and falls back to the Standard variant ID.
    this.preference = 'rts';
    this.map = {};
    this.currentStandardId = null;
    this.variantChangeUnsubscriber = null;
  }

  connectedCallback() {
    const mapNode = this.querySelector('[data-rts-variant-map]');
    if (!mapNode) return;
    try {
      this.map = JSON.parse(mapNode.textContent || '{}');
    } catch (err) {
      // Liquid should never emit invalid JSON, but be defensive — bad
      // data disables the toggle rather than crashing the PDP.
      console.warn('RTS toggle: invalid variant map', err);
      this.map = {};
    }

    this.form = document.getElementById(this.dataset.formId);
    this.input = this.form?.querySelector('input[name="id"]');
    this.fieldset = this.querySelector('[data-rts-fieldset]');
    if (!this.form || !this.input || !this.fieldset) return;

    this.currentStandardId = this.input.value;

    // Honor an explicit ?fulfillment= URL param over the constructor
    // default. Set by the Ready-to-Ship collection so cards landing on
    // the standard PDP pre-select RTS, and by anyone who wants to link
    // directly to a specific fulfillment option (marketing emails, etc.).
    try {
      const params = new URLSearchParams(window.location.search);
      const requested = params.get('fulfillment');
      if (requested === 'ready_to_ship') this.preference = 'rts';
      else if (requested === 'standard') this.preference = 'standard';
    } catch {
      // URLSearchParams unavailable → keep the constructor default.
    }

    // Reflect the resolved preference into the radio DOM before the
    // first applyState() so any keyboard-focused state matches.
    const initialRadio = this.querySelector(
      `[data-rts-option="${this.preference}"]`
    );
    if (initialRadio instanceof HTMLInputElement) initialRadio.checked = true;

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.dataset.rtsOption) {
        this.preference = target.dataset.rtsOption;
        this.applyState();
      }
    });

    // Theme publishes PUB_SUB_EVENTS.variantChange whenever the shopper
    // picks a new option combination. We subscribe rather than watching
    // the input directly because product-info.js also fires an input
    // 'change' event on the same input, which would create a feedback
    // loop if we listened there and re-wrote the value.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        (event) => {
          const variantId = event?.data?.variant?.id;
          const sectionId = event?.data?.sectionId;
          if (sectionId && this.dataset.sectionId && String(sectionId) !== String(this.dataset.sectionId)) {
            return;
          }
          if (variantId) {
            this.currentStandardId = String(variantId);
            this.applyState();
          }
        }
      );
    }

    this.applyState();
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
  }

  applyState() {
    if (!this.input || !this.fieldset) return;
    const rtsId = this.map[this.currentStandardId];

    if (!rtsId) {
      // No RTS twin for this variant — hide the toggle and force back
      // to Standard so the input holds the canonical variant ID.
      this.fieldset.hidden = true;
      this.preference = 'standard';
      const standardRadio = this.querySelector('[data-rts-option="standard"]');
      if (standardRadio instanceof HTMLInputElement) standardRadio.checked = true;
      if (this.currentStandardId) this.input.value = this.currentStandardId;
      return;
    }

    this.fieldset.hidden = false;
    const desired = this.preference === 'rts' ? 'rts' : 'standard';
    const desiredRadio = this.querySelector(`[data-rts-option="${desired}"]`);
    if (desiredRadio instanceof HTMLInputElement) desiredRadio.checked = true;

    if (desired === 'rts') {
      this.input.value = String(rtsId);
    } else if (this.currentStandardId) {
      this.input.value = this.currentStandardId;
    }
  }
}

customElements.define('rts-fulfillment-toggle', RtsFulfillmentToggle);
